import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { Gallery } from '../types';
import { supabase } from '../lib/supabase';

interface GalleryContextType {
  galleries: Gallery[];
  loading: boolean;
  error: string | null;
  getGallery: (id: number) => Gallery | undefined;
  addGallery: (gallery: Omit<Gallery, 'id' | 'created_at' | 'updated_at' | 'view_count'>) => Promise<void>;
  updateGallery: (id: number, gallery: Partial<Gallery>) => Promise<void>;
  deleteGallery: (id: number) => Promise<void>;
  incrementViewCount: (id: number) => Promise<void>;
  refreshGalleries: () => Promise<void>;
  ensureLoaded: () => void;
}

const GalleryContext = createContext<GalleryContextType | undefined>(undefined);

export const GalleryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 갤러리 데이터 가져오기
  const fetchGalleries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('galleries')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setGalleries(data || []);
    } catch (err) {
      console.error('Error fetching galleries:', err);
      setError(err instanceof Error ? err.message : '갤러리를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지연 로드: 앱 부팅 시 모든 Provider가 전체 테이블을 읽으면 방문자 1명당 요청 수십 건이 발생해
  // 접속이 몰릴 때 DB가 포화된다. 이 데이터를 쓰는 화면이 뜰 때만 한 번 읽는다.
  const loadedRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchGalleries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 특정 갤러리 가져오기
  const getGallery = (id: number) => {
    return galleries.find(gallery => gallery.id === id);
  };

  // 갤러리 추가
  const addGallery = async (gallery: Omit<Gallery, 'id' | 'created_at' | 'updated_at' | 'view_count'>) => {
    try {
      setError(null);

      const newGallery = {
        ...gallery,
        view_count: 0,
      };

      const { error: insertError } = await supabase
        .from('galleries')
        .insert([newGallery]);

      if (insertError) throw insertError;

      // 데이터 다시 불러오기
      await fetchGalleries();
    } catch (err) {
      console.error('Error adding gallery:', err);
      setError(err instanceof Error ? err.message : '갤러리 추가에 실패했습니다.');
      throw err;
    }
  };

  // 갤러리 수정
  const updateGallery = async (id: number, gallery: Partial<Gallery>) => {
    try {
      setError(null);

      const { error: updateError } = await supabase
        .from('galleries')
        .update(gallery)
        .eq('id', id);

      if (updateError) throw updateError;

      // 데이터 다시 불러오기
      await fetchGalleries();
    } catch (err) {
      console.error('Error updating gallery:', err);
      setError(err instanceof Error ? err.message : '갤러리 수정에 실패했습니다.');
      throw err;
    }
  };

  // 갤러리 삭제
  const deleteGallery = async (id: number) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('galleries')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // 데이터 다시 불러오기
      await fetchGalleries();
    } catch (err) {
      console.error('Error deleting gallery:', err);
      setError(err instanceof Error ? err.message : '갤러리 삭제에 실패했습니다.');
      throw err;
    }
  };

  // 조회수 증가
  const incrementViewCount = async (id: number) => {
    try {
      const gallery = getGallery(id);
      if (!gallery) return;

      const { error: updateError } = await supabase
        .from('galleries')
        .update({ view_count: gallery.view_count + 1 })
        .eq('id', id);

      if (updateError) throw updateError;

      // 로컬 상태 업데이트 (새로고침 없이)
      setGalleries(prev =>
        prev.map(g => g.id === id ? { ...g, view_count: g.view_count + 1 } : g)
      );
    } catch (err) {
      console.error('Error incrementing view count:', err);
      // 조회수 증가 실패는 에러로 처리하지 않음 (사용자 경험에 영향 없음)
    }
  };

  const refreshGalleries = async () => {
    await fetchGalleries();
  };

  return (
    <GalleryContext.Provider value={{
      ensureLoaded,
      galleries,
      loading,
      error,
      getGallery,
      addGallery,
      updateGallery,
      deleteGallery,
      incrementViewCount,
      refreshGalleries
    }}>
      {children}
    </GalleryContext.Provider>
  );
};

export const useGalleries = () => {
  const context = useContext(GalleryContext);
  // 이 훅을 쓰는 화면이 실제로 떴을 때만 조회를 시작한다(지연 로드).
  const ensureLoaded = context?.ensureLoaded;
  useEffect(() => {
    ensureLoaded?.();
  }, [ensureLoaded]);

  if (context === undefined) {
    throw new Error('useGalleries must be used within a GalleryProvider');
  }
  return context;
};
